import { Component, DOCUMENT, ElementRef, EventEmitter, HostListener, Inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';

import { ReducedMotion } from '../../motion/rise';
import { ModalFocusController } from '../drawer/modal-focus';

/** How far under the sheet's sticky head a group lands when a chip opens it (P/Member_Hub.html:935, `g.offsetTop - 64`). */
export const SHEET_GROUP_OFFSET_PX = 64;

/** The sheet's scrollTop that puts a group heading `SHEET_GROUP_OFFSET_PX` under the head, never above the top (:935). */
export function GroupHeadingOffset(headingTop: number, panelTop: number, currentScroll = 0): number {
    return Math.max(0, headingTop - panelTop + currentScroll - SHEET_GROUP_OFFSET_PX);
}

/** Makes each sheet's title id unique, for `aria-labelledby`. */
let nextSheetId = 0;

/**
 * The phone filter sheet: the prototype turns `.rail` into a bottom sheet while `html.mfilters-open` (P/Member_Hub.html:603-620,
 * 896-911) — fixed to the bottom edge, at most 84vh, a 16px top radius, a sticky "Filters / Done" head and a sticky 48px
 * "Show results" foot. Here the page projects its existing `agu-filter-rail` content (`<ng-content>`), and `Open` is the prototype's
 * `mfilters-open`.
 *
 * A modal dialog, on the drawer's pattern (`AGUDrawerComponent`): `role="dialog"` + `aria-modal`, focus moves to Done on open and a
 * document-level Tab listener (held only while open) keeps it inside, Escape and the scrim close, `body` and the nearest scrolling
 * ancestor (the surface's scrollport) are locked while open, and focus returns to the opener on close.
 *
 * `ScrollToGroup` names a group heading (the chip the member tapped, `OpenFilters(group)`): once open, the sheet scrolls so that
 * heading sits 64px under its head (:935) — smoothly, or at once under `prefers-reduced-motion`.
 */
@Component({
    standalone: false,
    selector: 'agu-filter-sheet',
    templateUrl: './filter-sheet.component.html',
    styleUrl: './filter-sheet.component.scss',
})
export class AGUFilterSheetComponent implements OnChanges, OnDestroy {
    @Input() Open = false;
    /** The group heading to scroll to on open, by its text; null opens at the top. */
    @Input() ScrollToGroup: string | null = null;
    /** What a group heading is inside the projected rail: the finder filter groups' labels. */
    @Input() HeadingSelector = '.ra-filters__label';

    @Output() Closed = new EventEmitter<void>();
    @Output() ShowResults = new EventEmitter<void>();

    /** The dialog element (the scroller); public so a spec can supply it without a TestBed. */
    @ViewChild('panel') Panel?: ElementRef<HTMLElement>;
    @ViewChild('doneButton') private doneButton?: ElementRef<HTMLButtonElement>;

    readonly TitleId = `agu-sheet-title-${(nextSheetId += 1)}`;

    /** Focus trap, scroll lock (body and the surface scrollport) and focus return, shared with the drawer. */
    private readonly modal: ModalFocusController;
    /** A re-scroll when `ScrollToGroup` changes while open; runs after the render that applies it. */
    private scrollTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(
        @Inject(DOCUMENT) document: Document,
        @Inject(ElementRef) private readonly host: ElementRef<HTMLElement> | null = null
    ) {
        this.modal = new ModalFocusController(document, () => this.Panel?.nativeElement ?? null, () => this.doneButton?.nativeElement ?? null);
    }

    ngOnChanges(changes: SimpleChanges): void {
        const open = changes['Open'];
        if (open && open.currentValue !== open.previousValue) {
            if (this.Open) this.Activate();
            else this.Deactivate();
            return;
        }
        if (changes['ScrollToGroup'] && this.Open) this.ScheduleScroll();
    }

    ngOnDestroy(): void {
        this.Deactivate();
    }

    RequestClose(): void {
        this.Closed.emit();
    }

    RequestShowResults(): void {
        this.ShowResults.emit();
    }

    /** Escape closes (P/Member_Hub.html:1020). */
    @HostListener('document:keydown.escape') OnEscape(): void {
        if (this.Open) this.RequestClose();
    }

    /** Tab and Shift+Tab stay inside the open sheet. */
    OnKeydown(event: KeyboardEvent): void {
        if (!this.Open) return;
        this.modal.OnKeydown(event);
    }

    /** Lock, trap, focus Done, then bring the asked-for group under the head. */
    private Activate(): void {
        this.modal.Activate(this.host?.nativeElement ?? null, () => this.ScrollToHeading());
    }

    private Deactivate(): void {
        this.ClearScrollTimer();
        this.modal.Deactivate();
    }

    private ScheduleScroll(): void {
        this.ClearScrollTimer();
        this.scrollTimer = setTimeout(() => {
            this.scrollTimer = null;
            this.ScrollToHeading();
        }, 0);
    }

    private ScrollToHeading(): void {
        const panel = this.Panel?.nativeElement;
        const group = this.ScrollToGroup?.trim();
        if (!panel) return;
        const behavior: ScrollBehavior = ReducedMotion() ? 'auto' : 'smooth';
        if (!group) {
            panel.scrollTo({ top: 0, behavior });
            return;
        }
        const heading = Array.from(panel.querySelectorAll<HTMLElement>(this.HeadingSelector)).find((el) => (el.textContent ?? '').trim() === group);
        if (!heading) return;
        const top = GroupHeadingOffset(heading.getBoundingClientRect().top, panel.getBoundingClientRect().top, panel.scrollTop);
        panel.scrollTo({ top, behavior });
    }

    private ClearScrollTimer(): void {
        if (this.scrollTimer !== null) {
            clearTimeout(this.scrollTimer);
            this.scrollTimer = null;
        }
    }
}
